import type { BillingAnalyticsReconciliation } from '../services/billingAnalyticsService';

export type BillingExportFormat = 'pdf' | 'csv' | 'excel';

export interface BillingExportMeta {
  startDate: string;
  endDate: string;
  generatedBy?: string;
  facilityName?: string;
}

export function localTodayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function formatDisplayDate(iso: string): string {
  if (!iso) {
    return '';
  }
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) {
    return iso;
  }
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(year, month - 1, day));
}

export function formatPeriodLabel(startDate: string, endDate: string): string {
  const start = formatDisplayDate(startDate);
  const end = formatDisplayDate(endDate);
  if (startDate === endDate) {
    return start;
  }
  return `${start} – ${end}`;
}

export function formatCurrencyInr(amount: number): string {
  if (!Number.isFinite(amount)) {
    return '₹0.00';
  }
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Compact value above bars — e.g. ₹26.9K, ₹1.4L */
export function formatBillingCompactCurrency(amount: number): string {
  if (!Number.isFinite(amount) || amount === 0) {
    return '₹0';
  }
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(1)}L`;
  }
  if (amount >= 1000) {
    return `₹${(amount / 1000).toFixed(1)}K`;
  }
  return formatCurrencyInr(amount);
}

/** Y-axis ticks — e.g. ₹0, ₹40K, ₹1.2L (matches collection reconciliation design) */
export function formatBillingAxisCurrency(amount: number): string {
  if (!Number.isFinite(amount) || amount === 0) {
    return '₹0';
  }
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(1)}L`;
  }
  if (amount >= 1000) {
    const thousands = amount / 1000;
    if (amount % 1000 === 0) {
      return `₹${thousands}K`;
    }
    return `₹${Math.round(thousands)}K`;
  }
  return formatCurrencyInr(amount);
}

export function formatGeneratedAt(iso?: string): string {
  const date = iso ? new Date(iso) : new Date();
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(date);
}

function sharePercent(amount: number, total: number): string {
  if (!total || !Number.isFinite(amount)) {
    return '0.00%';
  }
  return `${((amount / total) * 100).toFixed(2)}%`;
}

function escapeCsv(value: string): string {
  return `"${String(value).replace(/"/g, '""')}"`;
}

function downloadBlob(filename: string, content: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function buildBreakdownRows(
  rows: { label: string; amount: number; count?: number }[],
  totalAmount: number
): string {
  if (!rows.length) {
    return '<tr><td colspan="4" class="empty">No transactions found</td></tr>';
  }
  return rows
    .map(
      row => `<tr>
        <td>${row.label}</td>
        <td class="num">${formatCurrencyInr(row.amount)}</td>
        <td class="num">${row.count ?? 0}</td>
        <td class="num">${sharePercent(row.amount, totalAmount)}</td>
      </tr>`
    )
    .join('');
}

function buildReportStyles(): string {
  return `
    * { box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      color: #0f172a;
      margin: 0;
      padding: 24px;
      background: #f8fafc;
    }
    .report {
      max-width: 960px;
      margin: 0 auto;
      background: #fff;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      overflow: hidden;
    }
    .report-header {
      background: linear-gradient(135deg, #0e7490 0%, #0891b2 100%);
      color: #fff;
      padding: 24px 28px;
    }
    .report-header h1 {
      margin: 0 0 8px;
      font-size: 24px;
      font-weight: 700;
    }
    .report-meta {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px 24px;
      font-size: 13px;
      opacity: 0.95;
    }
    .report-body { padding: 24px 28px 32px; }
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(5, minmax(0, 1fr));
      gap: 12px;
      margin-bottom: 28px;
    }
    .summary-card {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 14px;
      background: #f8fafc;
    }
    .summary-card .label {
      font-size: 12px;
      color: #64748b;
      margin-bottom: 6px;
    }
    .summary-card .value {
      font-size: 18px;
      font-weight: 700;
      color: #0f172a;
    }
    .summary-card.net .value { color: #0e7490; }
    h2 {
      font-size: 16px;
      margin: 24px 0 10px;
      color: #0f172a;
      border-bottom: 2px solid #0891b2;
      padding-bottom: 6px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 8px;
      font-size: 13px;
    }
    th {
      background: #ecfeff;
      color: #155e75;
      text-align: left;
      padding: 10px 12px;
      border: 1px solid #cffafe;
      font-weight: 600;
    }
    td {
      padding: 9px 12px;
      border: 1px solid #e2e8f0;
    }
    td.num, th.num { text-align: right; }
    tr:nth-child(even) td { background: #f8fafc; }
    td.empty {
      text-align: center;
      color: #94a3b8;
      font-style: italic;
    }
    @media print {
      body { background: #fff; padding: 0; }
      .report { border: none; border-radius: 0; }
    }
  `;
}

export function buildReconciliationHtml(
  report: BillingAnalyticsReconciliation,
  meta: BillingExportMeta
): string {
  const { summary } = report;
  const totalForShare = summary.totalCollection || 1;

  const section = (
    title: string,
    rows: { label: string; amount: number; count?: number }[]
  ) => `<h2>${title}</h2>
    <table>
      <thead>
        <tr>
          <th>Name</th>
          <th class="num">Amount (INR)</th>
          <th class="num">Transactions</th>
          <th class="num">Share</th>
        </tr>
      </thead>
      <tbody>${buildBreakdownRows(rows, totalForShare)}</tbody>
    </table>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Collection Reconciliation Report</title>
  <style>${buildReportStyles()}</style>
</head>
<body>
  <div class="report">
    <div class="report-header">
      <h1>Collection Reconciliation Report</h1>
      <div class="report-meta">
        <div><strong>Period:</strong> ${formatPeriodLabel(meta.startDate, meta.endDate)}</div>
        <div><strong>Generated:</strong> ${formatGeneratedAt(report.generatedAt)}</div>
        ${meta.facilityName ? `<div><strong>Facility:</strong> ${meta.facilityName}</div>` : ''}
        ${meta.generatedBy ? `<div><strong>Generated by:</strong> ${meta.generatedBy}</div>` : ''}
      </div>
    </div>
    <div class="report-body">
      <div class="summary-grid">
        <div class="summary-card"><div class="label">Total collection</div><div class="value">${formatCurrencyInr(summary.totalCollection)}</div></div>
        <div class="summary-card"><div class="label">Refunds</div><div class="value">${formatCurrencyInr(summary.refunds)}</div></div>
        <div class="summary-card"><div class="label">Discounts</div><div class="value">${formatCurrencyInr(summary.discounts)}</div></div>
        <div class="summary-card net"><div class="label">Net collection</div><div class="value">${formatCurrencyInr(summary.netCollection)}</div></div>
        <div class="summary-card"><div class="label">Transactions</div><div class="value">${summary.transactionCount}</div></div>
      </div>
      ${section('Collection by users', report.byUser)}
      ${section('Collection by payment mode', report.byPaymentMode)}
      ${section('Collection by schemes', report.byScheme)}
      ${section('Collection by counters', report.byCounter)}
    </div>
  </div>
</body>
</html>`;
}

export function buildReconciliationCsv(
  report: BillingAnalyticsReconciliation,
  meta: BillingExportMeta
): string {
  const lines: string[] = [];
  const { summary } = report;
  const totalForShare = summary.totalCollection || 1;

  const appendSection = (
    title: string,
    rows: { label: string; amount: number; count?: number }[]
  ) => {
    lines.push('');
    lines.push(title.toUpperCase());
    lines.push('Name,Amount (INR),Transactions,Share (%)');
    if (!rows.length) {
      lines.push('No transactions found,,,');
      return;
    }
    rows.forEach(row => {
      lines.push(
        [
          escapeCsv(row.label),
          row.amount.toFixed(2),
          String(row.count ?? 0),
          sharePercent(row.amount, totalForShare),
        ].join(',')
      );
    });
  };

  lines.push('Collection Reconciliation Report');
  lines.push(`Report Period,${formatPeriodLabel(meta.startDate, meta.endDate)}`);
  lines.push(`Generated On,${formatGeneratedAt(report.generatedAt)}`);
  if (meta.facilityName) {
    lines.push(`Facility,${escapeCsv(meta.facilityName)}`);
  }
  if (meta.generatedBy) {
    lines.push(`Generated By,${escapeCsv(meta.generatedBy)}`);
  }
  lines.push('');
  lines.push('SUMMARY');
  lines.push('Metric,Amount (INR),Transactions');
  lines.push(`Total Collection,${summary.totalCollection.toFixed(2)},`);
  lines.push(`Refunds,${summary.refunds.toFixed(2)},`);
  lines.push(`Discounts,${summary.discounts.toFixed(2)},`);
  lines.push(`Net Collection,${summary.netCollection.toFixed(2)},`);
  lines.push(`Total Transactions,,${summary.transactionCount}`);

  appendSection('Collection by users', report.byUser);
  appendSection('Collection by payment mode', report.byPaymentMode);
  appendSection('Collection by schemes', report.byScheme);
  appendSection('Collection by counters', report.byCounter);

  return `\uFEFF${lines.join('\r\n')}`;
}

export function buildReconciliationExcelHtml(
  report: BillingAnalyticsReconciliation,
  meta: BillingExportMeta
): string {
  return buildReconciliationHtml(report, meta).replace(
    '<title>Collection Reconciliation Report</title>',
    '<title>Collection Reconciliation Report</title>\n<meta http-equiv="Content-Type" content="application/vnd.ms-excel; charset=UTF-8" />'
  );
}

export function exportReconciliationReport(
  format: BillingExportFormat,
  report: BillingAnalyticsReconciliation,
  meta: BillingExportMeta
): void {
  const baseName = `collection-reconciliation-${meta.startDate}_${meta.endDate}`;

  if (format === 'csv') {
    downloadBlob(
      `${baseName}.csv`,
      buildReconciliationCsv(report, meta),
      'text/csv;charset=utf-8;'
    );
    return;
  }

  if (format === 'excel') {
    downloadBlob(
      `${baseName}.xls`,
      buildReconciliationExcelHtml(report, meta),
      'application/vnd.ms-excel;charset=utf-8;'
    );
    return;
  }

  const html = buildReconciliationHtml(report, meta);
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.src = objectUrl;
  document.body.appendChild(iframe);

  iframe.onload = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      window.setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(objectUrl);
      }, 1000);
    }
  };
}

export function addShareToRows(
  rows: { label: string; amount: number; count?: number }[],
  totalAmount: number
): { label: string; amount: number; count: number; share: string }[] {
  return (rows || []).map(row => ({
    label: row.label,
    amount: row.amount,
    count: row.count ?? 0,
    share: sharePercent(row.amount, totalAmount),
  }));
}
